import {
  GET_COMPLIANCE_ANSWERS_URL,
  GET_COMPLIANCE_CHECKS_URL,
  GET_COMPLIANCE_COUNTS_URL,
  GET_COMPLIANCE_QUESTIONS_URL,
  get_headers,
  get_project_id
} from '@/utils/constants/constants.js';
import alert_mixin from '@/utils/mixins/alert_mixin';
import ComplianceMonitoring from '@/views/probaworkflows_base/management/compliance_management/compliance_monitoring/ComplianceMonitoring.vue';
import ComplianceNotifications from '@/views/probaworkflows_base/management/compliance_management/compliance_notifications/ComplianceNotifications.vue';
import axios from "axios";

export default {
  name: 'ComplianceOverview',
  mixins: [alert_mixin],
  props: {
    entityId: {
      type: Number,
      required: true
    }
  },
  components: {
    ComplianceMonitoring,
    ComplianceNotifications
  },
  data: () => ({
    headers: get_headers(),
    project_id: get_project_id(),
    questions: [],
    expanded_question_index: null,
    loading_questions: false,
    questions_pagination: {
      page: 1,
      page_size: 10,
      total_pages: 1,
      has_more: false,
      next_page: null
    },
    compliance_counts: {},
    counts: {
      compliant: 0,
      non_compliant: 0,
      pending: 0
    },
    profile_role_form_id: "",
    entity_type_id: "",
    active_tabs: ['Notifications', 'Forms']
  }),
  computed: {
    compliant_count() {
      return this.counts.compliant;
    },
    non_compliant_count() {
      return this.counts.non_compliant;
    },
    pending_count() {
      return this.counts.pending;
    },
    grouped_programs() {
      const programs_map = new Map();

      this.questions.forEach(question => {
        const form_name = question.form_name || 'Unnamed Form';
        const program_data = programs_map.get(form_name) || {
          id: `form-${form_name.replace(/\s+/g, '-').toLowerCase()}`,
          title: form_name,
          created_date: 'Jan 15, 2023',
          is_open: false,
          projects: []
        };
        program_data.projects.push({
          id: `question-${question.id}`,
          question_id: question.id,
          title: question.question_text,
          created: 'Apr 1, 2023',
          show_history: false,
          details: {},
          compliance_checks: [],
          question_data: question,
          section: question.section_name || '',
          form: question.form_name || '',
          compliance_status: question.compliance_status || 'PENDING',
        });

        programs_map.set(form_name, program_data);
      });

      return Array.from(programs_map.values());
    }
  },
  watch: {
    entityId: {
      immediate: true,
      handler(new_val) {
        if (new_val !== null && new_val !== undefined) {
          this.fetch_questions();
        }
      }
    }
  },
  mounted() {
    this.profile_role_form_id = this.$route.query.profile_role_form_id;
    this.entity_type_id = this.$route.query.entity_type_id;
    this.fetch_compliance_counts();
    this.fetch_questions();
  },
  methods: {
    toggle_tab(tab_name) {
      const index = this.active_tabs.indexOf(tab_name);
      if (index === -1) {
        this.active_tabs.push(tab_name);
      } else {
        this.active_tabs.splice(index, 1);
      }
    },
    calculate_counts() {
      this.counts = { compliant: 0, non_compliant: 0, pending: 0 };
      this.questions.forEach(question => {
        if (question.has_compliant_answers) this.counts.compliant++;
        else if (question.has_non_compliant) this.counts.non_compliant++;
        else this.counts.pending++;
      });
    },
    get_question_status_class(question) {
      if (question.has_compliant_answers) return 'bg-success text-white';
      if (question.has_non_compliant) return 'bg-danger text-white';
      return 'bg-warning text-dark';
    },
    get_question_status_text(question) {
      const count = question.answers.length || 0;
      const text = `${count} file${count !== 1 ? 's' : ''}`;
      if (question.has_compliant_answers) return `${text} - Compliant`;
      if (question.has_non_compliant) return `${text} - Non-compliant`;
      return `${text} - Pending`;
    },
    get_history_status_class(check) {
      return check.status === 'COMPLIANT' ? 'border-success' : 'border-danger';
    },
    get_history_badge_class(status) {
      switch (status) {
        case 'COMPLIANT':
          return 'bg-success text-white';
        case 'NON_COMPLIANT':
          return 'bg-danger text-white';
        case 'PENDING':
          return 'bg-warning text-dark';
        case 'GRACE_PERIOD':
          return 'bg-info text-white';
        case 'WAIVED':
          return 'bg-secondary text-white';
        default:
          return 'bg-secondary text-white';
      }
    },
    // Replaced get_history_status_label method
    get_history_status_label(status) {
      switch (status) {
        case 'COMPLIANT':
          return 'Compliant';
        case 'NON_COMPLIANT':
          return 'Non-Compliant';
        case 'PENDING':
          return 'Pending';
        case 'GRACE_PERIOD':
          return 'Grace Period';
        case 'WAIVED':
          return 'Waived';
        default:
          return 'Unknown Status';
      }
    },
    fetch_compliance_counts() {
      this.loading_questions = true;
      const payload = {
        project_id: this.project_id,
        profile_role_form_id: this.profile_role_form_id
      };

      axios.post(GET_COMPLIANCE_COUNTS_URL, payload, { headers: this.headers })
        .then(response => {
          if (response.data.status === "Success") {
            this.compliance_counts = response.data.data;
          } else {
            this.compliance_counts = {}
            // this.show_error(response.data.message || 'Failed to fetch compliance counts');
          }
        })
        .catch(() => {
          this.compliance_counts = {}
          // this.show_error('Error fetching compliance counts: ' + error.message);
        })
        .finally(() => {
          this.loading_questions = false;
        });
    },
    process_questions_response(response, page) {
      const pagination = response.data.data.pagination;
      const question_details = response.data.data.question_details;

      const new_questions = question_details.map(q => ({
        ...q,
        expanded: false,
        loading_answers: false,
        answers: [],
        answers_pagination: {
          page: 1,
          page_size: 5,
          has_more: false,
          next_page: null
        },
        compliance_status: q.compliance_status || 'PENDING',
      }));

      if (page === 1) {
        this.questions = new_questions;
      } else {
        this.questions.push(...new_questions);
      }

      this.questions_pagination = {
        page: pagination.page_number,
        page_size: this.questions_pagination.page_size,
        total_pages: pagination.total_pages,
        has_more: pagination.next_page !== null,
        next_page: pagination.next_page
      };
      this.calculate_counts();
    },
    fetch_questions(page = 1) {
      this.loading_questions = true;
      const payload = {
        project_id: this.project_id,
        profile_role_form_id: this.profile_role_form_id,
        page_number: page,
        page_size: this.questions_pagination.page_size
      };

      axios.post(GET_COMPLIANCE_QUESTIONS_URL, payload, { headers: this.headers })
        .then(response => {
          if (response.data.status === "Success") {
            this.process_questions_response(response, page);
          } else {
            this.question_data = []
            // this.show_error(response.data.message || 'Failed to fetch compliance questions');
          }
        })
        .catch(() => {
          this.question_data = []
          // this.show_error('Error fetching compliance questions: ' + error.message);
        })
        .finally(() => {
          this.loading_questions = false;
        });
    },
    toggle_question_expansion(question_id) {
      if (!question_id) return;
      
      if (this.expanded_question_id === question_id) {
        this.expanded_question_id = null;
        return;
      }
      
      this.expanded_question_id = question_id;
      const question = this.questions.find(q => q.id === question_id);
      
      if (question && Array.isArray(question.answers) && question.answers.length === 0) {
        this.fetch_answers_for_question(question);
      }
    },
    process_answers_response(question, response, page) {
      const pagination = response.data.data.pagination;
      const answer_details = response.data.data.answer_details;

      const new_answers = answer_details.map(a => ({
        ...a,
        show_history: false,
        loading_checks: false,
        checks: [],
        checks_pagination: {
          page: 1,
          page_size: 3,
          has_more: false,
          next_page: null
        }
      }));

      if (page === 1) {
        question.answers = new_answers;
      } else {
        question.answers.push(...new_answers);
      }

      question.answers_pagination = {
        page: pagination.page_number,
        page_size: question.answers_pagination.page_size,
        has_more: pagination.next_page !== null,
        next_page: pagination.next_page
      };
    },
    fetch_answers_for_question(question, page = 1) {
      question.loading_answers = true;
      const payload = {
        project_id: this.project_id,
        profile_role_form_id: this.profile_role_form_id,
        section_question_id: question.id,
        page_number: page,
        page_size: question.answers_pagination.page_size
      };

      axios.post(GET_COMPLIANCE_ANSWERS_URL, payload, { headers: this.headers })
        .then(response => {
          if (response.data.status === "Success") {
            this.process_answers_response(question, response, page);
          } else {
            this.show_error(response.data.message || 'Failed to fetch answers');
          }
        })
        .catch(error => {
          this.show_error('Error fetching answers: ' + error.message);
        })
        .finally(() => {
          question.loading_answers = false;
        });
    },
    toggle_answer_history(question, answer) {
      answer.show_history = !answer.show_history;
      if (answer.show_history && answer.checks.length === 0) {
        this.fetch_checks_for_answer(question, answer);
      }
    },
    process_checks_response(answer, response, page) {
      const pagination = response.data.data.pagination;
      const check_details = response.data.data.check_details;

      if (page === 1) {
        answer.checks = check_details;
      } else {
        answer.checks.push(...check_details);
      }

      answer.checks_pagination = {
        page: pagination.page_number,
        page_size: answer.checks_pagination.page_size,
        has_more: pagination.next_page !== null,
        next_page: pagination.next_page
      };
    },
    fetch_checks_for_answer(answer, page = 1) {
      answer.loading_checks = true;
      const payload = {
        project_id: this.project_id,
        profile_role_form_id: this.profile_role_form_id,
        answer_id: answer.id,
        page_number: page,
        page_size: answer.checks_pagination.page_size
      };

      axios.post(GET_COMPLIANCE_CHECKS_URL, payload, { headers: this.headers })
        .then(response => {
          if (response.data.status === "Success") {
            this.process_checks_response(answer, response, page);
          } else {
            this.show_error(response.data.message || 'Failed to fetch compliance checks');
          }
        })
        .catch(error => {
          this.show_error('Error fetching compliance checks: ' + error.message);
        })
        .finally(() => {
          answer.loading_checks = false;
        });
    },
    extract_file_name(answer) {
      if (!answer) return 'No file';

      let url_string = '';
      if (typeof answer === 'string') {
        url_string = answer;
      } else if (answer.filename) {
        return answer.filename;
      } else if (answer.url) {
        url_string = answer.url;
      } else {
        return 'Unknown file';
      }

      try {
        const clean_url = url_string.split('?')[0];
        const url = new URL(clean_url);
        const file_path = url.pathname;
        return decodeURIComponent(file_path.split('/').pop());
      } catch {
        return 'Invalid file URL';
      }
    },
    format_date(date_string) {
      if (!date_string) return 'N/A';
      const date = new Date(date_string);
      return date.toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    fetch_answers(section_question_id, page = 1, page_size = 5) {
      const payload = {
        project_id: this.project_id,
        profile_role_form_id: this.profile_role_form_id,
        section_question_id,
        page_number: page,
        page_size
      };

      return axios.post(GET_COMPLIANCE_ANSWERS_URL, payload, { headers: this.headers })
        .then(response => {
          if (response.data.status === "Success") {
            return response.data.data.answer_details || [];
          }
          return [];
        })
        .catch(() => {
          return [];
        });
    },
    fetch_checks(answer_id, page = 1, page_size = 3) {
      const payload = {
        project_id: this.project_id,
        profile_role_form_id: this.profile_role_form_id,
        answer_id,
        page_number: page,
        page_size
      };

      return axios.post(GET_COMPLIANCE_CHECKS_URL, payload, { headers: this.headers })
        .then(response => {
          if (response.data.status === "Success") {
            return response.data.data.check_details || [];
          }
          return [];
        })
        .catch(() => {
          return [];
        });
    }
  }
};